import React, { useMemo, useState } from 'react'

import clsx from 'clsx'

import Tooltip from 'components/Tooltip'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import { thousandSeparator } from 'helpers/formatters'
import useViewport from 'hooks/useViewport'

import ChartContainer from '../../UsersAndUseCases/components/ChartContainer'

import styles from './BarChart.module.scss'

const LABELS = {
  small_businesses: 'Small Businesses',
  mid_size_businesses: 'Midsize Businesses',
  enterprises: 'Enterprises',
}

const EMPTY_KEYS = ['small_businesses', 'mid_size_businesses', 'enterprises']

const BarChart = ({ data, title = 'Company size' }) => {
  const { total, ...charts } = data || {}
  const [hoveredBar, setHoveredBar] = useState(null)
  const { up } = useViewport()
  const isDesktop = up('sm')
  const { sendEvent } = useEventContext({ name: 'engagement_hover' })

  const isEmpty =
    !data ||
    (Array.isArray(data) && data.length === 0) ||
    (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0) ||
    Object.keys(charts).length === 0 ||
    Object.values(charts).every(v => !v)

  const processedData = useMemo(() => {
    if (isEmpty) return []

    const maxItem = Object.entries(charts).reduce(
      (max, [key, value]) => (value > max.value ? { name: key, value } : max),
      { name: '', value: 0 }
    )

    return Object.entries(charts)
      .map(([key, value]) => {
        return {
          name: key,
          value,
          isHighest: value === maxItem.value,
          percentage: total > 0 ? Math.round((value / total) * 100) : 0,
          heightPercentage: (value / total) * 100,
        }
      })
      .sort((a, b) => b.percentage - a.percentage)
  }, [charts, total, isEmpty])

  return (
    <ChartContainer
      title={title}
      divider={isDesktop ? 'right' : 'bottom'}
      className={styles.chartContainer}
      isEmpty={isEmpty}
    >
      <div className={styles.root}>
        {isEmpty
          ? EMPTY_KEYS.map(key => (
              <div key={key} className={styles.item}>
                <div className={styles.barWrapper}>
                  <div className={clsx(styles.bar, styles.empty)} />
                  <Typography className={clsx(styles.barLabel, styles.empty)}>{LABELS[key]}</Typography>
                </div>
              </div>
            ))
          : processedData.map((item, index) => (
              <div
                key={item.name}
                role="button"
                onMouseEnter={() => {
                  if (isEmpty) return
                  setHoveredBar(item.name)

                  sendEvent({
                    engagementComponent: 'product_users-and-use-cases',
                    engagementDestination: 'show_business-size-details',
                    engagementDetails: 'hover_business-size-graph',
                    name: 'engagement_hover',
                  })
                }}
                onMouseLeave={() => !isEmpty && setHoveredBar(null)}
                className={styles.item}
              >
                <Tooltip title={` ${item.percentage}% (${thousandSeparator(item.value)} reviews)`} placement="top">
                  <div className={styles.barWrapper}>
                    <div
                      className={clsx(styles.bar, {
                        [styles.hovered]: !isEmpty && hoveredBar === item.name,
                        [styles.dimmed]: !isEmpty && hoveredBar && hoveredBar !== item.name,
                      })}
                    >
                      <div
                        className={clsx(styles.barFill, styles[`barFill-${index + 1}`])}
                        style={{ height: `${item.heightPercentage}%` }}
                      />
                    </div>
                    <Typography
                      className={clsx(
                        styles.barLabel,
                        { [styles.hovered]: !isEmpty && hoveredBar === item.name },
                        { [styles.dimmed]: !isEmpty && hoveredBar && hoveredBar !== item.name }
                      )}
                    >
                      {LABELS[item.name]}
                    </Typography>
                  </div>
                </Tooltip>
              </div>
            ))}
      </div>
    </ChartContainer>
  )
}

export default BarChart
